import { AirspaceFeatureType } from "@nova/constants"

type RGB = [number, number, number]
type RGBA = [number, number, number, number]

const BASE_COLORS = {
    restriction: {
        fill: [142, 142, 142],
        stroke: [255, 255, 255],
    },
    notam: {
        fill: [246, 198, 41],
        stroke: [246, 198, 41],
    },
    tfr: {
        fill: [255, 76, 76],
        stroke: [255, 76, 76],
    },
} as const satisfies Record<AirspaceFeatureType, { fill: RGB; stroke: RGB }>

const LIGHT_STROKE_OVERRIDES: Partial<Record<AirspaceFeatureType, RGB>> = {
    restriction: [0, 0, 0],
}

type OpacitySet = [
    excludedFromListOrInactive: number,
    includedInListNotHovered: number,
    normal: number,
    hoverOrActive: number,
]
const opacities = {
    restriction: {
        fill: [5, 16, 26, 38],
        stroke: [20, 61, 102, 217],
    },
    notam: {
        fill: [5, 16, 26, 38],
        stroke: [26, 77, 128, 255],
    },
    tfr: {
        fill: [5, 16, 26, 38],
        stroke: [26, 77, 128, 255],
    },
} as const satisfies Record<AirspaceFeatureType, { fill: OpacitySet; stroke: OpacitySet }>

type FeatureContext = {
    hoveredIds: Set<string>
    activeId: string | undefined
    // Ids of airspaces shown in the currently open list popup (of this feature's type), if any
    listIds: Set<string> | undefined
}

type FeatureVisualState = { opacityLevel: number; active: boolean }

const getFeatureVisualState =
    ({ hoveredIds, activeId, listIds }: FeatureContext) =>
    (feature: GeoJSON.Feature): FeatureVisualState => {
        if (feature.id == null) return { opacityLevel: 2, active: false }
        const id = `${feature.id}`

        if (activeId != null) {
            const active = id === activeId
            return { opacityLevel: active ? 3 : 0, active }
        }
        if (listIds) {
            if (!listIds.has(id)) return { opacityLevel: 0, active: false }
            return { opacityLevel: hoveredIds.has(id) ? 3 : 1, active: false }
        }
        return { opacityLevel: hoveredIds.has(id) ? 3 : 2, active: false }
    }

export const getFillColor =
    ({ featureType, ...context }: FeatureContext & { featureType: AirspaceFeatureType }) =>
    (feature: GeoJSON.Feature): RGBA => {
        const { opacityLevel } = getFeatureVisualState(context)(feature)
        const [r, g, b] = BASE_COLORS[featureType].fill
        return [r, g, b, opacities[featureType].fill[opacityLevel]]
    }

export const getLineColor =
    ({
        featureType,
        isLight,
        ...context
    }: FeatureContext & { featureType: AirspaceFeatureType; isLight: boolean }) =>
    (feature: GeoJSON.Feature): RGBA => {
        const { opacityLevel } = getFeatureVisualState(context)(feature)
        const [r, g, b] =
            (isLight && LIGHT_STROKE_OVERRIDES[featureType]) || BASE_COLORS[featureType].stroke
        return [r, g, b, opacities[featureType].stroke[opacityLevel]]
    }

const DASHED = [8, 5] as [number, number]
const SOLID = [0, 0] as [number, number]
export const getDashArray = (context: FeatureContext) => (feature: GeoJSON.Feature) =>
    getFeatureVisualState(context)(feature).active ? SOLID : DASHED

const strokeWidths = [1, 2] as const
export const getLineWidth = (context: FeatureContext) => (feature: GeoJSON.Feature) =>
    strokeWidths[getFeatureVisualState(context)(feature).active ? 1 : 0]
